import { Component, inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CapabilityControllerService } from '../../api/capability-service';
import { CapabilityCreateRequest } from '../../api/capability-service/model/capabilityCreateRequest';
import { PermissionCheckerService } from '../../services/permission/permission-checker.service';

/**
 * Phase 2b — new capability dialog. Creates as IN_PROGRESS and navigates
 * to /administration/capabilities/:code/:version so the operator can
 * add fields next.
 */
@Component({
    selector: 'app-capability-create-dialog',
    standalone: false,
    template: `
<div class="bw-dialog cap-create-dialog">
    <lib-page-header screenName="Capability" title="New capability" backTo="">
        <mat-icon icon>add_circle</mat-icon>
        <ng-container subtitle>
            <span>Drafts start as IN_PROGRESS; promote to ACTIVE when the contract is finalized.</span>
        </ng-container>
        <ng-container actions>
            <button mat-icon-button (click)="cancel()" matTooltip="Cancel" aria-label="Cancel">
                <mat-icon>close</mat-icon>
            </button>
        </ng-container>
    </lib-page-header>

    <div class="bw-body">
        <form [formGroup]="form" class="form-grid">
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Code</mat-label>
                <input matInput formControlName="code"
                       placeholder="e.g. ne.firmware.audit" required maxlength="64" />
                <mat-hint>Stable identifier; tasks pin to (code, version).</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Version</mat-label>
                <input matInput formControlName="version"
                       placeholder="1.0.0" required maxlength="20" />
                <mat-hint>Semver, e.g. 1.0.0 / 2.1.3</mat-hint>
                <mat-error *ngIf="form.get('version')?.errors?.['pattern']">
                    Must be semver (major.minor.patch)
                </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description (optional)</mat-label>
                <textarea matInput formControlName="description" rows="3"
                          placeholder="One-line summary surfaced in the schedule-creation wizard"></textarea>
            </mat-form-field>
        </form>
    </div>

    <div class="bw-footer">
        <button mat-button (click)="cancel()">Cancel</button>
        <button mat-flat-button color="primary" [disabled]="form.invalid || saving()"
                (click)="save()">Create draft</button>
    </div>
</div>
`,
    styles: [`
.bw-body { padding: 16px 24px; }
.bw-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 24px; border-top: 1px solid var(--border-divider); }
.form-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
`],
})
export class CapabilityCreateDialogComponent {

    private ref: MatDialogRef<CapabilityCreateDialogComponent> = inject(MatDialogRef);
    private router: Router = inject(Router);
    private fb: FormBuilder = inject(FormBuilder);
    private capabilityService: CapabilityControllerService = inject(CapabilityControllerService);
    private permissionChecker: PermissionCheckerService = inject(PermissionCheckerService);

    form: FormGroup = this.fb.group({
        code:        ['', [Validators.required, Validators.maxLength(64)]],
        version:     ['', [Validators.required, Validators.pattern(/^\d+\.\d+\.\d+$/)]],
        description: [''],
    });

    saving = signal(false);

    cancel(): void { this.ref.close(null); }

    save(): void {
        if (this.form.invalid) return;
        if (!this.permissionChecker.hasAccessToCreateCapability()) { return; }
        this.saving.set(true);
        const body: CapabilityCreateRequest = {
            code:        this.form.value.code,
            version:     this.form.value.version,
            description: this.form.value.description || undefined,
        };
        this.capabilityService.create1(body).subscribe({
            next: (rec: any) => {
                this.saving.set(false);
                this.ref.close(rec);
                this.router.navigate(['/administration/capabilities', rec.code, rec.version]);
            },
            error: (e: any) => {
                this.saving.set(false);
                alert(`Create failed: ${typeof e === 'string' ? e : JSON.stringify(e)}`);
            },
        });
    }
}
